import React, { useState, useEffect } from 'react';
import { message } from 'antd';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiService, Assessment } from '../services/apiService';
import { useTerminology } from '../hooks/useTerminology';
import './AssessmentStyles.css';

const AssessmentList: React.FC = () => {
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [filteredAssessments, setFilteredAssessments] = useState<Assessment[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const navigate = useNavigate();
  const { isAdmin, isAssessor } = useAuth();
  const t = useTerminology();

  const { user } = useAuth();

  useEffect(() => {
    loadAssessments();
  }, []);

  // Refresh assessments when user changes (for team member updates)
  useEffect(() => {
    if (user) {
      loadAssessments();
    }
  }, [user?.username, user?.tenantId]);

  useEffect(() => {
    filterAssessments();
  }, [assessments, searchText, statusFilter, typeFilter]);

  const loadAssessments = async () => {
    try {
      setLoading(true);
      const data = await apiService.getAssessments();
      setAssessments(data);
    } catch (error) {
      console.error('Error loading assessments:', error);
      message.error('No Assessment have been created');
    } finally {
      setLoading(false);
    }
  };

  const filterAssessments = () => {
    let filtered = [...assessments];

    // Search filter
    if (searchText) {
      filtered = filtered.filter(assessment =>
        assessment.assessmentName.toLowerCase().includes(searchText.toLowerCase()) ||
        assessment.assessorName.toLowerCase().includes(searchText.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(assessment => assessment.status === statusFilter);
    }

    // Type filter
    if (typeFilter !== 'all') {
      filtered = filtered.filter(assessment => assessment.assessmentType === typeFilter);
    }

    setFilteredAssessments(filtered);
  };

  const handleDelete = async (assessmentId: string) => {
    try {
      await apiService.deleteAssessment(assessmentId);
      message.success('Assessment deleted successfully');
      loadAssessments();
    } catch (error) {
      console.error('Error deleting assessment:', error);
      message.error('Failed to delete assessment');
    }
  };

  const handleExport = async (assessmentId: string, format: 'excel' | 'pdf') => {
    try {
      const result = await apiService.exportAssessment(assessmentId, format);
      window.open(result.fileUrl, '_blank');
      message.success(`Assessment exported as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Error exporting assessment:', error);
      message.error('Failed to export assessment');
    }
  };

  return (
    <div>
      {/* Page Header */}
      <div className="v3-page-header">
        <div className="v3-page-title">Assessments</div>
        {isAssessor && (
          <div className="v3-header-actions">
            <button className="v3-btn v3-btn-primary" type="button" onClick={() => navigate('/assessments/new?type=CMDA')}>
              + New CMDA Assessment
            </button>
            <button className="v3-btn v3-btn-outline" type="button" onClick={() => navigate('/assessments/new?type=CMA')}>
              + New CMA Assessment
            </button>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="v3-filters">
        <div className="v3-search-wrap">
          <span className="v3-search-icon">&#128269;</span>
          <input
            type="text"
            className="v3-search-input"
            placeholder="Search assessments..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>
        <select
          className="v3-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Status</option>
          <option value="draft">Draft</option>
          <option value="submitted">Submitted</option>
        </select>
        <select
          className="v3-select"
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          <option value="all">All Types</option>
          <option value="CMDA">CMDA</option>
          <option value="CMA">CMA</option>
        </select>
      </div>

      {/* Table */}
      <div className="v3-table-wrap">
        {loading ? (
          <div className="v3-empty-state">
            <div className="v3-empty-text">Loading assessments...</div>
          </div>
        ) : filteredAssessments.length === 0 ? (
          <div className="v3-empty-state">
            <div className="v3-empty-icon">&#128196;</div>
            <div className="v3-empty-text">No assessments found</div>
          </div>
        ) : (
          <table className="v3-table">
            <thead>
              <tr>
                <th>Assessment Name</th>
                <th>Type</th>
                <th>Assessor</th>
                <th>Status</th>
                <th>Score</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAssessments.map((assessment) => (
                <tr key={assessment.assessmentId}>
                  <td className="td-name">{assessment.assessmentName}</td>
                  <td>
                    <span className={`v3-badge ${assessment.assessmentType === 'CMDA' ? 'v3-badge-cmda' : 'v3-badge-cma'}`}>
                      {assessment.assessmentType}
                    </span>
                  </td>
                  <td>{assessment.assessorName}</td>
                  <td>
                    <span className={`v3-badge ${
                      assessment.status === 'submitted' ? 'v3-badge-submitted' : 'v3-badge-draft'
                    }`}>
                      {assessment.status === 'submitted' ? 'Submitted' : 'Draft'}
                    </span>
                  </td>
                  <td>
                    {assessment.overallScore ? (
                      <span className={`v3-badge ${
                        assessment.overallScore === 'Adequate' ? 'v3-badge-adequate' : 'v3-badge-inadequate'
                      }`}>
                        {assessment.overallScore === 'Adequate' ? t.rating_adequate : t.rating_inadequate}
                      </span>
                    ) : (
                      <span style={{ color: 'var(--v3-gray-400)', fontSize: 12 }}>--</span>
                    )}
                  </td>
                  <td style={{ color: 'var(--v3-gray-500)', fontSize: 12 }}>
                    {new Date(assessment.createdAt).toLocaleDateString()}
                  </td>
                  <td>
                    <span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <a className="v3-action-link" onClick={() => navigate(`/assessments/${assessment.assessmentId}`)}>
                        View
                      </a>
                      {isAssessor && assessment.status === 'draft' && (
                        <a className="v3-action-link" onClick={() => navigate(`/assessments/${assessment.assessmentId}/edit`)}>
                          Edit
                        </a>
                      )}
                      {assessment.status === 'submitted' && (
                        <>
                          <a className="v3-action-link" onClick={() => handleExport(assessment.assessmentId, 'excel')}>
                            Excel
                          </a>
                          <a className="v3-action-link" onClick={() => handleExport(assessment.assessmentId, 'pdf')}>
                            PDF
                          </a>
                        </>
                      )}
                      {isAdmin && (
                        <a className="v3-action-link" style={{ color: 'var(--v3-danger)' }} onClick={() => {
                          if (window.confirm('Are you sure you want to delete this assessment?')) {
                            handleDelete(assessment.assessmentId);
                          }
                        }}>
                          Delete
                        </a>
                      )}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default AssessmentList;
